// Закрытие назначения последним днём. Панель, а не модалка: назначение не удаляется, у него
// проставляется дата окончания, и её при ошибке правят переназначением — необратимого нет.
//
// Последний день — это ДЕНЬ, который человек ещё отработал, а не день, с которого он не
// работает. Разница в один день здесь означает одну лишнюю или одну недостающую смену в
// табеле, поэтому подпись под полем говорит об этом прямо, а не «дата окончания».

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';

import { useCloseAssignmentMutation } from '../queries';

const EDIT_PERMISSION = 'core.employee.edit';

export function CloseAssignmentPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);

  const [dateTo, setDateTo] = useState(todayIso);
  const [dateError, setDateError] = useState<string | undefined>(undefined);
  const [gateNote, setGateNote] = useState<string | null>(null);
  const mutation = useCloseAssignmentMutation();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Закрывать назначения может тот, у кого есть право на правку карточек. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Назначение уже закрывается, ждём ответ сервера.');
      return;
    }
    if (dateTo === '') {
      setDateError('Укажите последний день: без него назначение так и останется живым.');
      return;
    }
    setDateError(undefined);
    setGateNote(null);
    mutation.mutate({ assignmentId: id, dateTo }, { onSuccess: onClose });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: назначение не закроется. Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      <p className='text-paragraph-sm text-text-sub-600'>
        Назначение перестанет быть живым: человек уйдёт из укомплектованности объекта и из новых
        табелей. Уже сданные табели и ведомости не трогаются.
      </p>

      <DateField
        id='close-assignment-date'
        label='Последний рабочий день'
        required
        value={dateTo}
        onChange={setDateTo}
        error={dateError}
        hint='Именно последний отработанный день, а не первый нерабочий: разница в один день — это одна смена в табеле.'
      />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='error' mode='stroke' size='small'>
          {mutation.isPending ? 'Закрываем…' : 'Закрыть назначение'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
